import React, { useState } from 'react';
import { Recipe, Ingredient } from '../../../types/recipe';
import { IngredientRow } from './IngredientRow';
import { createIngredient } from '../../../store/slices/recipesSlice';

interface RecipeCardProps {
  recipe: Recipe;
  components: { id: string; name: string }[];
  onUpdateName: (id: string, name: string) => void;
  onUpdateYield: (id: string, yieldValue: number) => void;
  onUpdateIngredients: (id: string, ingredients: Ingredient[]) => void;
  onDelete?: (id: string) => void;
  onUpdateProductCode?: (id: string, productCode: string) => void;
  showProductCode?: boolean;
}

export const RecipeCard: React.FC<RecipeCardProps> = ({
  recipe,
  components,
  onUpdateName,
  onUpdateYield,
  onUpdateIngredients,
  onDelete,
  onUpdateProductCode,
  showProductCode = false
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(recipe.name);
  const [isEditingProductCode, setIsEditingProductCode] = useState(false);
  const [tempProductCode, setTempProductCode] = useState(recipe.productCode || '');
  const [ingredients, setIngredients] = useState<Ingredient[]>(recipe.ingredients);

  const handleNameClick = () => {
    setIsEditingName(true);
    setTempName(recipe.name);
  };

  const handleNameSave = () => {
    const newName = tempName.trim() || recipe.name;
    onUpdateName(recipe.id, newName);
    setTempName(newName);
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setTempName(recipe.name);
      setIsEditingName(false);
    }
  };

  const handleProductCodeClick = () => {
    setIsEditingProductCode(true);
    setTempProductCode(recipe.productCode || '');
  };

  const handleProductCodeSave = () => {
    const newProductCode = tempProductCode.trim();
    if (!newProductCode) {
      alert('Product code is required');
      return;
    }
    if (onUpdateProductCode) {
      onUpdateProductCode(recipe.id, newProductCode);
    }
    setTempProductCode(newProductCode);
    setIsEditingProductCode(false);
  };

  const handleProductCodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleProductCodeSave();
    } else if (e.key === 'Escape') {
      setTempProductCode(recipe.productCode || '');
      setIsEditingProductCode(false);
    }
  };

  const handleYieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (value && value >= 1) {
      onUpdateYield(recipe.id, value);
    }
  };

  const handleIngredientChange = (index: number, updated: Ingredient) => {
    const newIngredients = [...ingredients];
    newIngredients[index] = updated;
    setIngredients(newIngredients);
    onUpdateIngredients(recipe.id, newIngredients);
  };

  const handleRemoveIngredient = (index: number) => {
    const newIngredients = ingredients.filter((_, i) => i !== index);
    setIngredients(newIngredients);
    onUpdateIngredients(recipe.id, newIngredients);
  };

  const handleAddIngredient = () => {
    const newIngredient = createIngredient();
    const newIngredients = [...ingredients, newIngredient];
    setIngredients(newIngredients);
    onUpdateIngredients(recipe.id, newIngredients);
  };

  const handleAddComponent = () => {
    const newComponent = createIngredient('', 0, '', 'component');
    const newIngredients = [...ingredients, newComponent];
    setIngredients(newIngredients);
    // Don't save to backend until component is selected from dropdown
    // onUpdateIngredients will be called by handleIngredientChange when dropdown changes
  };

  return (
    <div
      className="recipe-bubble"
      style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '16px',
        backgroundColor: 'white'
      }}
    >
      {isEditingName ? (
        <input
          type="text"
          className="edit-input"
          value={tempName}
          onChange={e => setTempName(e.target.value)}
          onBlur={handleNameSave}
          onKeyDown={handleNameKeyDown}
          autoFocus
          style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            border: '1px solid #4CAF50',
            borderRadius: '4px',
            padding: '4px 8px',
            width: '100%',
            marginBottom: '16px'
          }}
        />
      ) : (
        <h3
          className="editable-header"
          onClick={handleNameClick}
          style={{
            cursor: 'pointer',
            fontSize: '1.5rem',
            marginBottom: '16px',
            color: '#333'
          }}
        >
          {recipe.name}
        </h3>
      )}

      {showProductCode && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '4px', fontSize: '0.9rem' }}>
            Product Code <span style={{ color: 'red' }}>*</span>
          </label>
          {isEditingProductCode ? (
            <input
              type="text"
              className="edit-input"
              value={tempProductCode}
              onChange={e => setTempProductCode(e.target.value.toUpperCase())}
              onBlur={handleProductCodeSave}
              onKeyDown={handleProductCodeKeyDown}
              placeholder="e.g., FILL-001"
              required
              autoFocus
              style={{
                fontSize: '1rem',
                border: '1px solid #4CAF50',
                borderRadius: '4px',
                padding: '6px 10px',
                width: '200px'
              }}
            />
          ) : (
            <div
              className="editable-text"
              onClick={handleProductCodeClick}
              style={{
                cursor: 'pointer',
                fontSize: '1rem',
                padding: '6px 10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                display: 'inline-block',
                backgroundColor: recipe.productCode ? 'white' : '#fff3cd',
                color: recipe.productCode ? '#333' : '#856404',
                width: '200px'
              }}
            >
              {recipe.productCode || 'Click to add product code'}
            </div>
          )}
        </div>
      )}

      <div className="recipe-section">
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Ingredients</label>

        <div className="ingredients-container">
          {ingredients.map((ingredient, index) => (
            <IngredientRow
              key={ingredient.id}
              ingredient={ingredient}
              components={components}
              onChange={updated => handleIngredientChange(index, updated)}
              onRemove={() => handleRemoveIngredient(index)}
              isComponent={ingredient.type === 'component'}
            />
          ))}

          {ingredients.length === 0 && (
            <IngredientRow
              ingredient={createIngredient()}
              components={components}
              onChange={updated => handleIngredientChange(0, updated)}
              onRemove={() => {}}
              isComponent={false}
            />
          )}
        </div>

        <div className="ingredient-buttons" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <button
            type="button"
            className="add-ingredient-btn"
            onClick={handleAddIngredient}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            + Add Ingredient
          </button>

          <button
            type="button"
            className="add-component-btn"
            onClick={handleAddComponent}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            + Add Component
          </button>
        </div>
      </div>

      <div className="yield-section" style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <label className="yield-label" style={{ fontWeight: 'bold' }}>
          Makes:
        </label>
        <input
          type="number"
          className="yield-input"
          value={recipe.yield}
          onChange={handleYieldChange}
          min="1"
          step="1"
          style={{
            width: '80px',
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />
        <span className="yield-unit">units</span>
      </div>

      {onDelete && (
        <div style={{ marginTop: '16px' }}>
          <button
            type="button"
            onClick={() => onDelete(recipe.id)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Delete Recipe
          </button>
        </div>
      )}
    </div>
  );
};
